import { createSelector } from 'reselect';
import { AppPlatform, Integration, IntegrationPlatform, MobileAppsReducerState } from './types';

export type STATE = { mobileApps: MobileAppsReducerState };
type PROPS = { id: string };

const getMobileApps = (state: STATE) => state.mobileApps.apps;
const getIntegrations = (state: STATE) => state.mobileApps.integrations;

export const getMobileAppById = (state: STATE, props: PROPS) =>
  state.mobileApps.apps[props.id] || {};
export const getIsFetchingMobileApps = (state: STATE) => state.mobileApps.isFetchingMobileApps;
export const getIsFetchingIntegrations = (state: STATE) => state.mobileApps.isFetchingIntegrations;
export const getUnassignedIntegrations = (state: STATE) =>
  state.mobileApps.integrations?.unassigned || [];
export const getMobileAppIntegrationsById = (state: STATE, props: PROPS) =>
  state.mobileApps.integrations[props.id];
export const getIsCreateOrUpdateIntegrationInProgress = (state: STATE) =>
  state.mobileApps.createOrUpdateIntegrationInProgress;
export const getIsCreateOrUpdateIntegrationError = (state: STATE) =>
  state.mobileApps.createOrUpdateIntegrationError;

export const selectMobileApps = createSelector([getMobileApps], (mobileApps) =>
  Object.values(mobileApps).sort((l, r) => (l.name.toLowerCase() > r.name.toLowerCase() ? 1 : -1)),
);

export const selectIntegrations = createSelector([getIntegrations], (integrations) => {
  const { unassigned, ...assigned } = integrations;
  return [
    ...(unassigned ?? []),
    ...Object.values(assigned).reduce((prev: Integration[], next) => {
      const flattened = Object.values(next).flat();
      return [...prev, ...flattened];
    }, []),
  ];
});

/* selectMobileAppsWithIntegrations
Select iOS and Android apps that have a push integration */
export const selectMobileAppsWithIntegrations = createSelector(
  [selectMobileApps, getIntegrations],
  (mobileApps, integrations) =>
    Object.values(mobileApps.filter((app) => !!integrations[app.id])).sort((l, r) => l.id - r.id),
);

export const selectFilteredUnassignedIntegrations = createSelector(
  [getUnassignedIntegrations, getMobileAppById],
  (unassignedIntegrations, mobileApp) =>
    unassignedIntegrations.filter((integration) =>
      mobileApp.platform === AppPlatform.ANDROID
        ? integration.platform === IntegrationPlatform.GCM
        : integration.platform !== IntegrationPlatform.GCM,
    ),
);

export const selectAreAllIntegrationSlotsAssigned = createSelector(
  [getIntegrations, getMobileAppById],
  (integrations, mobileApp) => {
    const { id, platform } = mobileApp || {};
    const assignedIntegrations = integrations[id] || {};
    return platform === AppPlatform.ANDROID
      ? !!assignedIntegrations[IntegrationPlatform.GCM]
      : !!assignedIntegrations[IntegrationPlatform.APNS] &&
          !!assignedIntegrations[IntegrationPlatform.APNS_SANDBOX];
  },
);
